import React, { useRef, useState, useEffect } from 'react';
import { Box, Button, Paper, Typography } from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

/**
 * SignaturePad Component
 * Canvas-based signature capture without external dependencies
 */
const SignaturePad = ({ onSave, onClear, width = 500, height = 200 }) => {
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [isEmpty, setIsEmpty] = useState(true);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (canvas) {
      const ctx = canvas.getContext('2d');
      ctx.strokeStyle = '#000';
      ctx.lineWidth = 2;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
    }
  }, []);

  const startDrawing = (e) => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();

    const x = (e.clientX || e.touches?.[0]?.clientX) - rect.left;
    const y = (e.clientY || e.touches?.[0]?.clientY) - rect.top;

    ctx.beginPath();
    ctx.moveTo(x, y);
    setIsDrawing(true);
    setIsEmpty(false);
  };

  const draw = (e) => {
    if (!isDrawing) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();

    const x = (e.clientX || e.touches?.[0]?.clientX) - rect.left;
    const y = (e.clientY || e.touches?.[0]?.clientY) - rect.top;

    ctx.lineTo(x, y);
    ctx.stroke();
  };

  const stopDrawing = () => {
    setIsDrawing(false);
  };

  const handleClear = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setIsEmpty(true);

    if (onClear) {
      onClear();
    }
  };

  const handleSave = () => {
    if (isEmpty) {
      return;
    }

    const canvas = canvasRef.current;
    const dataURL = canvas.toDataURL('image/png');

    // Extract base64 data (remove data:image/png;base64, prefix)
    const base64Data = dataURL.split(',')[1];

    if (onSave) {
      onSave(base64Data);
    }
  };

  return (
    <Paper elevation={2} sx={{ p: 2, display: 'inline-block' }}>
      <Typography variant="subtitle2" gutterBottom>
        Sign in the box below
      </Typography>

      <Box
        sx={{
          border: '2px dashed #ccc',
          borderRadius: 1,
          mb: 2,
          display: 'inline-block',
          cursor: 'crosshair',
          touchAction: 'none'
        }}
      >
        <canvas
          ref={canvasRef}
          width={width}
          height={height}
          onMouseDown={startDrawing}
          onMouseMove={draw}
          onMouseUp={stopDrawing}
          onMouseLeave={stopDrawing}
          onTouchStart={startDrawing}
          onTouchMove={draw}
          onTouchEnd={stopDrawing}
          style={{
            display: 'block',
            backgroundColor: '#fff'
          }}
        />
      </Box>

      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
        <Button
          variant="outlined"
          startIcon={<ClearIcon />}
          onClick={handleClear}
          disabled={isEmpty}
        >
          Clear
        </Button>
        <Button
          variant="contained"
          startIcon={<CheckCircleIcon />}
          onClick={handleSave}
          disabled={isEmpty}
          color="success"
        >
          Save Signature
        </Button>
      </Box>
    </Paper>
  );
};

export default SignaturePad;
